import { Download, Eye, Lock, Pencil, X } from 'lucide-react';
import { Button } from '@/shared/components';
import type { PropertyFileResponse } from '@/shared/api';
import { formatBytes, formatCurrency, formatDate } from '@/shared/lib/format';
import { AkteFileThumbnail } from './AkteFileThumbnail';
import { folderDisplayPath } from './akteFolderNames';
import { PROPERTY_DOCUMENT_KIND_SHORT } from './akteKindLabels';
import { lockedTooltip, type FileActions } from './akteFileActions';

interface Props {
  propertyId: string;
  file: PropertyFileResponse;
  actions: FileActions;
  onClose: () => void;
}

/** Inhalt der Detail-Pane: Vorschau, Metadaten und die nächsten Aktionen zur gewählten Datei. */
export function AkteDetailPane({ propertyId, file, actions, onClose }: Props) {
  const typeLabel =
    file.contentType === 'application/pdf'
      ? 'PDF'
      : (file.contentType.split('/').pop()?.toUpperCase() ?? 'Datei');

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-ink-faint text-xs font-medium uppercase tracking-wider">Detail</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Detail schließen"
          className="text-ink-faint hover:text-ink -mr-2 flex h-11 w-11 items-center justify-center rounded"
        >
          <X size={18} />
        </button>
      </div>

      {/* Die Vorschau ist das offensichtlichste Ziel — sie öffnet, was der Button darunter öffnet. */}
      <button
        type="button"
        onClick={() => actions.onPreview(file)}
        aria-label={`${file.fileName} in der Vorschau öffnen`}
        className="bg-card border-line hover:border-sealwax mb-3 flex items-center justify-center rounded-lg border p-4 transition-colors"
      >
        <AkteFileThumbnail propertyId={propertyId} file={file} width={160} />
      </button>

      <p className="text-ink break-words font-serif text-lg" title={file.fileName}>
        {file.fileName}
      </p>
      <p className="text-ink-faint mb-3 text-xs">
        {typeLabel} · {formatBytes(file.sizeBytes)}
      </p>

      <dl className="mb-4 space-y-2 text-sm">
        <Row label="Kategorie">
          <span className="bg-sealwax/10 text-sealwax rounded px-1.5 py-0.5 text-xs font-medium">
            {PROPERTY_DOCUMENT_KIND_SHORT[file.kind]}
          </span>
        </Row>
        {/* Wo die Datei liegt, war bisher nur im Breadcrumb sichtbar — nach einer Suche über den
            ganzen Baum ist das die wichtigste fehlende Auskunft. */}
        <Row label="Ablage" title={file.folder}>
          {folderDisplayPath(file.folder)}
        </Row>
        {file.issuedOn && <Row label="Beleg-Datum">{formatDate(file.issuedOn)}</Row>}
        {file.amount != null && file.currency && (
          <Row label="Betrag">{formatCurrency(file.amount, file.currency)}</Row>
        )}
        {file.counterparty && (
          <Row label="Gegenpartei" title={file.counterparty}>
            {file.counterparty}
          </Row>
        )}
        <Row label="Hochgeladen">{formatDate(file.uploadedAt)}</Row>
      </dl>

      {file.locked && (
        <p className="text-sealwax bg-sealwax/10 mb-3 flex items-start gap-1.5 rounded-md px-2.5 py-2 text-xs">
          <Lock size={13} className="mt-0.5 shrink-0" />
          Gesperrter Beleg (GoBD). Aufbewahrung bis{' '}
          {file.retentionUntil ? formatDate(file.retentionUntil) : 'unbefristet'}.
        </p>
      )}

      <div className="mt-auto space-y-2 pb-[env(safe-area-inset-bottom)]">
        <Button
          variant="primary"
          className="w-full"
          leftIcon={<Eye size={14} />}
          onClick={() => actions.onPreview(file)}
        >
          Vorschau
        </Button>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            className="flex-1"
            leftIcon={<Download size={14} />}
            onClick={() => actions.onDownload(file)}
          >
            Herunterladen
          </Button>
          <Button
            variant="ghost"
            className="flex-1"
            leftIcon={<Pencil size={14} />}
            disabled={file.locked}
            title={file.locked ? lockedTooltip(file) : undefined}
            onClick={() => actions.onEdit(file)}
          >
            Bearbeiten
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  title,
  children,
}: {
  label: string;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ink-faint shrink-0">{label}</dt>
      <dd className="text-ink min-w-0 truncate text-right" title={title}>
        {children}
      </dd>
    </div>
  );
}
